import { APP_INITIALIZER, ApplicationRef, Injectable, Injector } from '@angular/core';
import { SwRegistrationOptions, SwUpdate, VersionReadyEvent } from '@angular/service-worker';

import { filter, first, map } from 'rxjs/operators';

import { ConfigFacade } from '@rx-applications/clientsite/shared/config/data-access';
import { environment } from '@rx-applications/clientsite/shared/utils';
import { LoggingFacade } from '@rx-applications/clientsite/core/logging';

const enum SWStrategy {
  RegisterWhenStable = 'registerWhenStable', // Register as soon as the application stabilizes
  RegisterImmediately = 'registerImmediately', // Register immediately
  RegisterWithDelay = 'registerWithDelay', // Usage: `${SWStrategy.RegisterWithDelay}:5000` // Register with a delay of 5000 milliseconds
}

@Injectable({ providedIn: 'root' })
export class PwaConfig {
  constructor(private updates: SwUpdate, private injector: Injector, private loggingFacade: LoggingFacade) {}

  load(): void {
    if (this.updates.isEnabled) {
      this.handleUpdates();
      this.handleUnrecoverableState();
      this.checkForUpdate();
    }
  }

  private handleUpdates(): void {
    this.updates.versionUpdates
      .pipe(first((event) => !!event && event.type === 'VERSION_READY'))
      .subscribe((event: VersionReadyEvent) => {
        this.loggingFacade.logEventToDatadog('New Version Detected', event);
        window.location.reload();
      });
  }

  private handleUnrecoverableState(): void {
    this.updates.unrecoverable.subscribe((event) => {
      this.loggingFacade.logEventToDatadog('Unrecoverable State', event);
      window.location.reload();
    });
  }

  private checkForUpdate(): void {
    const appRef = this.injector.get(ApplicationRef);
    appRef.isStable.pipe(first((stable) => stable)).subscribe(() => {
      this.updates
        .checkForUpdate()
        .then((newVersionDetected) => !!newVersionDetected && this.loggingFacade.logEventToDatadog('Checking for Updates'))
        // eslint-disable-next-line no-console
        .catch(console.error);
    });
  }
}

export function loadServiceWorkerOptions(config: ConfigFacade): SwRegistrationOptions {
  return {
    enabled: environment.production,
    registrationStrategy: () =>
      config.isPwaEnabled$.pipe(
        filter((value) => value),
        map(() => SWStrategy.RegisterWhenStable)
      ),
  };
}

// required for AOT
export function pwaMonitoring(config: PwaConfig) {
  return () => config.load();
}

export const PWA_PROVIDERS = [
  {
    provide: SwRegistrationOptions,
    useFactory: loadServiceWorkerOptions,
    deps: [ConfigFacade],
  },
  {
    provide: APP_INITIALIZER,
    useFactory: pwaMonitoring,
    deps: [PwaConfig, LoggingFacade],
    multi: true,
  },
];
